(() => {
  const host = document.getElementById('hue-mini-review-module');
  if (!host) return;

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_ENDPOINTS = [/\/cart\.js$/, /\/cart\/add\.js$/, /\/cart\/change\.js$/, /\/cart\/update\.js$/, /\/cart\/clear\.js$/];

  /* -------------------
   * Helpers
   * ------------------- */
  function extractVariantId(raw) {
    if (!raw) return null;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const m = raw.match(/(\d+)\s*$/);
      return m ? Number(m[1]) : Number(raw);
    }
    return null;
  }

  async function getCartJson() {
    try {
      const r = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  }

  function setCountOnEl(el, count) {
    // text/value
    if (el.matches('input,[contenteditable="true"]')) el.value = String(count);
    else el.textContent = String(count);

    // common data attributes different themes watch
    el.setAttribute('data-cart-count', String(count));
    const bubble = el.closest('[data-cart-count-bubble], .cart-count-bubble, .cart-bubble');
    if (bubble) bubble.setAttribute('data-cart-count', String(count));

    // aria label on the clickable parent
    const btn = el.closest('button,[role="button"],a');
    if (btn) {
      const base = (btn.getAttribute('aria-label') || btn.title || 'Cart').replace(/\s*\(\d+\)\s*$/, '');
      const label = `${base} (${count})`;
      btn.setAttribute('aria-label', label);
      if (!btn.title) btn.title = label;
    }
  }

  function updateCartCountDOM(count) {
    const selectors = [
      // Fast themes / Frame
      '.f-drawer__header [data-cart-count]',
      '.f-drawer__header .cart-count',
      '.f-drawer__header [data-cart-count-bubble]',
      // Header / opener
      '[data-drawer-opener] [data-cart-count]',
      '[data-drawer-opener] [data-cart-count-bubble]',
      '.drawer-opener [data-cart-count]',
      '.drawer-opener .cart-count',
      '.drawer-opener [data-cart-count-bubble]',
      // Common Dawn-like
      '.header__icon--cart [data-cart-count]',
      '.header__icon--cart .cart-count-bubble',
      '[data-cart-count-bubble]',
      '.cart-count',
      '.cart-bubble'
    ];
    const targets = selectors.flatMap(s => Array.from(document.querySelectorAll(s)));
    targets.forEach(el => setCountOnEl(el, count));

    // Toggle is-empty if your theme uses it
    document.querySelectorAll('cart-drawer, cart-notification').forEach(n => {
      n.classList?.toggle?.('is-empty', count === 0);
    });
  }

  let refreshInFlight = false;
  async function refreshCartCount() {
    if (refreshInFlight) return;
    refreshInFlight = true;
    try {
      const cart = await getCartJson();
      if (cart && typeof cart.item_count === 'number') updateCartCountDOM(cart.item_count);
    } finally {
      refreshInFlight = false;
    }
  }

  // Debounce helper for noisy observers
  let debounceT;
  function debouncedRefresh(ms = 60) {
    clearTimeout(debounceT);
    debounceT = setTimeout(refreshCartCount, ms);
  }

  /* -------------------
   * Theme sections / UI
   * ------------------- */
  const cartUI = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  function getSectionsList() {
    const ids = cartUI?.getSectionsToRender?.().map(s => s.id);
    if (ids && ids.length) return ids;
    return ['cart-drawer', 'cart-icon-bubble', 'CartDrawer', 'CartIconBubble'];
  }

  // Ensure we always refresh after theme's render
  if (cartUI?.renderContents) {
    const _render = cartUI.renderContents.bind(cartUI);
    cartUI.renderContents = (state) => { _render(state); debouncedRefresh(); };
  }

  /* -------------------------
   * Hue → Shopify add-to-cart
   * ------------------------- */
  document.addEventListener('hue-add-to-cart', async (e) => {
    const d = e?.detail || {};
    const variantId = extractVariantId(d.variantId);
    if (!variantId) {
      console.warn('[Hue mini] Missing/invalid variantId', d);
      return;
    }

    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', '1');

    const sections = getSectionsList().filter(Boolean);
    if (sections.length) {
      fd.append('sections', sections.join());
      fd.append('sections_url', location.pathname);
    }

    let resp;
    try {
      resp = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' }
      });
    } catch (err) {
      console.error('[Hue mini] add.js network error:', err);
      return;
    }
    if (!resp.ok) {
      console.error('[Hue mini] add.js failed', resp.status, await resp.text().catch(() => ''));
      return;
    }

    const state = await resp.json().catch(() => null);
    if (cartUI?.renderContents && state) {
      cartUI.classList?.remove?.('is-empty');
      cartUI.renderContents(state);
    } else {
      document.dispatchEvent(new CustomEvent('cart:build',   { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: state }));
    }

    debouncedRefresh();

    // Open single drawer/opener if present
    (document.querySelector('[data-drawer-opener]') ||
     document.querySelector('.drawer-opener') ||
     document.querySelector('a.cart-trigger'))?.click?.();
  }, { passive: true });

  /* -----------------------------
   * Listen to common theme events
   * ----------------------------- */
  document.addEventListener('cart:refresh', debouncedRefresh, { passive: true });
  document.addEventListener('cart:build',   debouncedRefresh, { passive: true });
  document.addEventListener('shopify:section:load', debouncedRefresh, { passive: true });
  document.addEventListener('shopify:section:reorder', debouncedRefresh, { passive: true });
  document.addEventListener('product:added', debouncedRefresh, { passive: true });
  document.addEventListener('cart:updated', debouncedRefresh, { passive: true });

  // If the theme exposes a context bus (seen in your logs)
  try {
    const themeCtx = window.__Theme?._ctx || window.theme?.Cart?.drawer;
    if (themeCtx?.on) {
      themeCtx.on('cart-atc', debouncedRefresh);
      themeCtx.on('cart-item-added', debouncedRefresh);
      themeCtx.on('cart-updated', debouncedRefresh);
    }
  } catch {}

  /* -------------------------------------
   * Intercept cart network calls (add/del)
   * ------------------------------------- */
  // fetch()
  const _fetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const res = await _fetch(...args);
    try {
      const url = (args[0] && args[0].url) || args[0] || '';
      if (CART_ENDPOINTS.some(rx => rx.test(String(url)))) {
        // wait for body to be consumed by caller, then refresh
        queueMicrotask(() => debouncedRefresh());
      }
    } catch {}
    return res;
  };

  // XMLHttpRequest
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;
  let _isCartCall = false;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    _isCartCall = CART_ENDPOINTS.some(rx => rx.test(String(url)));
    return _open.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function(...args) {
    if (_isCartCall) {
      this.addEventListener('loadend', () => debouncedRefresh(), { once: true });
    }
    return _send.apply(this, args);
  };

  /* ---------------------------------------------------
   * DOM observers: when theme swaps badge/drawer nodes
   * --------------------------------------------------- */
  const bodyObserver = new MutationObserver((muts) => {
    // Only react when nodes relevant to header/drawer are touched
    if (muts.some(m => (m.addedNodes && m.addedNodes.length) || (m.attributeName))) {
      debouncedRefresh();
    }
  });
  bodyObserver.observe(document.documentElement || document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'open', 'aria-hidden', 'data-cart-count']
  });

  // Drawer visibility toggles
  const drawer = document.querySelector('cart-drawer, .f-drawer');
  if (drawer) {
    const mo = new MutationObserver(() => debouncedRefresh());
    mo.observe(drawer, { attributes: true, attributeFilter: ['open', 'class', 'aria-hidden'] });
  }

  // Initial sync
  refreshCartCount();
})();